import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ConfirmDialog from "../../components/dialog/ConfirmDialog";
import NoDataMsg from "../info/NoDataMsg";
import { TbAlertOctagonFilled } from "react-icons/tb";
import { FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import { useState } from "react";
import {
    FaCheckCircle,
    FaCalendarAlt,
    FaClock,
    FaTrashAlt
} from "react-icons/fa";
import RoundedButton from "../buttons/RoundedButton";
import ActionBody from "./components/ActionBody";
import FormDialog from "../dialog/FormDialog";
import TextAreaWithCounter from "../inputs/TextAreaWithCounter";
import { Checkbox } from "primereact/checkbox";
import { getToday } from "../../utils/analytics";
import { MdDelete } from "react-icons/md";
import { MdCancel } from "react-icons/md";
import { PiNewspaperClippingFill } from "react-icons/pi";

const NEWS_TYPES = [
    { value: "info", label: "Informativa" },
    { value: "warning", label: "Importante" },
];

const NEWS_TYPE_STYLES = {
    info: {
        label: "Informativa",
        icon: FaInfoCircle,
        bg: "#DBEAFE",    // Azul claro
        text: "#1D4ED8"   // Azul oscuro
    },
    warning: {
        label: "Importante",
        icon: FaExclamationTriangle,
        bg: "#FEF3C7",    // Amarillo claro
        text: "#B45309"   // Amarillo oscuro
    }
};

const DURATION_OPTIONS = [
    { value: 3, label: "3 días" },
    { value: 7, label: "7 días" },
    { value: 15, label: "15 días" },
    { value: 30, label: "30 días" }
];

const emptyNewSetting = {
    title: "",
    description: "",
    type: "info",
    scheduleBool: false,
    schedule: null,
    durationDays: 7,
};

const STATUS_STYLES = {
    active: {
        label: "Activa",
        icon: FaCheckCircle,
        bg: "#DCFCE7",    // Verde claro
        text: "#15803D"   // Verde oscuro
    },
    scheduled: {
        label: "Programada",
        icon: FaCalendarAlt,
        bg: "#DBEAFE",    // Azul claro
        text: "#1D4ED8"   // Azul oscuro
    },
    expired: {
        label: "Expirada",
        icon: FaClock,
        bg: "#FEF3C7",    // Amarillo claro
        text: "#B45309"   // Naranja oscuro
    },
    deleted: {
        label: "Eliminada",
        icon: MdCancel,
        bg: "#FEE2E2",    // Rojo claro
        text: "#B91C1C"   // Rojo oscuro
    }
};

function TableNews({
    news,
    saveNew = () => { },
    deleteNew = () => { }
}) {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newsToDelete, setNewsToDelete] = useState(null);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newSetting, setNewSetting] = useState(emptyNewSetting);
    const [editingNewsId, setEditingNewsId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const handleSaveNew = async () => {
        const res = await saveNew(newSetting, editingNewsId);
        if (res) {
            setCreateDialogOpen(false);
            setNewSetting(emptyNewSetting);
            setEditingNewsId(null);
            setIsEditing(false);
        }
    };

    const handleEditClick = (newsItem) => {
        setNewSetting({
            title: newsItem.title,
            description: newsItem.description,
            type: newsItem.type,
            scheduleBool: !!newsItem.published_at,
            schedule: newsItem.published_at || null,
            durationDays: newsItem.duration_days,
        });
        setEditingNewsId(newsItem.id);
        setIsEditing(true);
        setCreateDialogOpen(true);
    };

    return (
        <>
            {news?.length > 0 ? (
                <div className="card p-0" aria-label="table"
                    style={{
                        borderRadius: "12px",
                        overflow: "hidden",
                    }}
                >
                    <DataTable
                        value={news}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        filterDisplay="row"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                        filter
                        responsiveLayout="scroll"
                    >
                        <Column
                            field="title"
                            header={"Contenido"}
                            filter
                            filterMatchMode={["contains"]}
                            showFilterMenu={false}
                            body={(rowData) => (
                                <div className="d-flex flex-column gap-1">
                                    <span>{rowData.title}</span>
                                    <small className="text-muted">{rowData.description || "- Sin descripción -"}</small>
                                </div>
                            )}
                            style={{ minWidth: "30rem" }}
                        />

                        <Column
                            field="type"
                            header="Tipo"
                            sortable
                            body={(rowData) => {
                                const styles = NEWS_TYPE_STYLES[rowData.type];

                                if (!styles) return (<p>{rowData.type}</p>);

                                const IconComponent = styles.icon;

                                return (
                                    <div className="d-inline-flex align-items-center gap-2">
                                        <span
                                            className="entity-values-badge d-inline-flex align-items-center gap-1"
                                            style={{
                                                backgroundColor: styles.bg,
                                                borderColor: styles.border || "transparent",
                                                color: styles.text,
                                            }}
                                        >
                                            <IconComponent />
                                            <span>{styles.label}</span>
                                        </span>
                                    </div>
                                );
                            }}
                        />

                        <Column
                            field="duration_days"
                            header={"Duración"}
                            showFilterMenu={false}
                            sortable
                            body={(rowData) => (
                                <span>{rowData.duration_days} dias</span>
                            )}
                        />

                        <Column
                            field="status"
                            header="Estado"
                            sortable
                            body={(rowData) => {
                                const styles = STATUS_STYLES[rowData.status];

                                if (!styles) return null;

                                const IconComponent = styles.icon;

                                return (
                                    <div className="d-inline-flex align-items-center gap-2">
                                        <span
                                            className="entity-values-badge d-inline-flex align-items-center gap-1"
                                            style={{
                                                backgroundColor: styles.bg,
                                                borderColor: styles.border || "transparent",
                                                color: styles.text,
                                            }}
                                        >
                                            <IconComponent />
                                            <span>{styles.label}</span>
                                        </span>
                                    </div>
                                );
                            }}
                        />

                        <Column
                            field="created_at"
                            header={"Fecha de creación"}
                            showFilterMenu={false}
                            sortable
                            body={(rowData) => (
                                <div className="d-flex flex-column gap-1">
                                    {/* <div>
                                        <span className="entity-values-badge">{rowData.created_at.relative}</span>
                                    </div> */}
                                    <small className="">{rowData.created_at.date + " - " + rowData.created_at.time + "hs" || "- Sin fecha -"}</small>
                                </div>
                            )}
                            style={{ minWidth: "15rem" }}
                        />

                        <Column
                            field="published_at"
                            header={"Fecha de publicación"}
                            showFilterMenu={false}
                            sortable
                            body={(rowData) => (
                                <div className="d-flex flex-column gap-1">
                                    {/* <div>
                                        <span className="entity-values-badge">{rowData.published_at.relative}</span>
                                    </div> */}
                                    <small className="">{rowData.published_at.date + " - " + rowData.published_at.time + "hs" || "- Sin fecha -"}</small>
                                </div>
                            )}
                            style={{ minWidth: "15rem" }}
                        />

                        <Column
                            header={<RoundedButton label="+ Agregar" handleClick={setCreateDialogOpen} />}
                            body={(rowdata) => {
                                if (rowdata.status === "deleted") return null;
                                return (
                                    <div className="d-flex flex-column justify-content-center align-items-end me-2">
                                        <ActionBody
                                            data={rowdata}
                                            toggleDelete={() => {
                                                setNewsToDelete(rowdata);
                                                setDeleteDialogOpen(true);
                                            }}
                                            toggleEdit={() => handleEditClick(rowdata)}
                                        />
                                    </div>
                                );
                            }}

                            className={"actionheader p-2"}
                            style={{ minWidth: "1rem" }}
                        ></Column>
                    </DataTable>
                </div>
            ) : (
                <NoDataMsg
                    title="Sin novedades"
                    subtitle="Aún no tienes publicaciones creadas."
                    icon={<PiNewspaperClippingFill />}
                    buttons={[
                        <RoundedButton
                            color="trini"
                            label="Agregar Novedad"
                            onClick={() => {
                                setCreateDialogOpen(true);
                            }}
                        />
                    ]}
                />
            )}

            <FormDialog
                visible={createDialogOpen}
                closeDialog={() => {
                    setCreateDialogOpen(false);
                    setNewSetting(emptyNewSetting);
                    setEditingNewsId(null);
                    setIsEditing(false);
                }}
                title={isEditing ? "Editar Novedad" : "Agregar Novedad"}
                acceptDialog={handleSaveNew}
            >
                <div className="d-flex flex-column gap-3">
                    <div className="field">
                        <label className={"ms-3"} htmlFor="newIntent">
                            Tipo
                        </label>
                        <select
                            value={newSetting.type || null}
                            onChange={(e) => {
                                setNewSetting({ ...newSetting, type: e.target.value });
                            }}
                            required
                        >
                            {NEWS_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label className={"ms-3"} htmlFor="newTitle">
                            Titulo
                        </label>
                        <input
                            value={newSetting.title}
                            onChange={(e) => {
                                setNewSetting({ ...newSetting, title: e.target.value });
                            }}
                            required
                            maxLength={100}
                            placeholder="Ingresa un título.."
                        />
                    </div>
                    <div className="field">
                        <label className={"ms-3"} htmlFor="newDescription">
                            Descripción
                        </label>
                        <TextAreaWithCounter
                            value={newSetting.description}
                            onChange={(e) => {
                                setNewSetting({ ...newSetting, description: e.target.value });
                            }}
                            required
                            maxLength={1000}
                            placeholder="Ingresa una descripción.."
                        />
                    </div>
                    {!isEditing && (
                        <>
                            <div className="my-3 p-2 border" style={{ borderRadius: "10px" }}>
                                <div className="row djaCenter ps-2">
                                    <div className="col-10">
                                        <h6>Programar Publicacion</h6>
                                    </div>
                                    <div className="col-2 djaEnd">
                                        <Checkbox
                                            id="schedule-check"
                                            aria-label="checkbox"
                                            onChange={() => {
                                                setNewSetting({
                                                    ...newSetting,
                                                    scheduleBool: !newSetting.scheduleBool,
                                                });
                                            }}
                                            checked={newSetting.scheduleBool}
                                        ></Checkbox>
                                    </div>
                                </div>
                                {newSetting.scheduleBool && (
                                    <div className="mt-3 mb-0">
                                        <input
                                            id="schedule"
                                            type="datetime-local"
                                            min={getToday()}
                                            value={newSetting.schedule}
                                            onChange={(e) => {
                                                setNewSetting({ ...newSetting, schedule: e.target.value });
                                            }}
                                            style={{
                                                lineHeight: "19px",
                                                overflow: "hidden",
                                                height: "42px",
                                                borderRadius: "6px",
                                            }}
                                            placeholder="Ingresa un Fecha y Hora"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="field">
                                <label className={"ms-3"} htmlFor="durationDays">
                                    Duración
                                </label>
                                <select
                                    id="durationDays"
                                    value={newSetting.durationDays || 7}
                                    onChange={(e) => {
                                        setNewSetting({ ...newSetting, durationDays: parseInt(e.target.value) });
                                    }}
                                    required
                                >
                                    {DURATION_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </FormDialog>

            <ConfirmDialog
                visible={deleteDialogOpen}
                title="Eliminar Novedad"
                onClose={() => {
                    setDeleteDialogOpen(false);
                }}
                icon={<MdDelete size={40} />}
                iconBackgroundColor="red"
                confirmColor="danger"
                onConfirm={() => {
                    deleteNew(newsToDelete?.id);
                    setDeleteDialogOpen(false);
                }}
                description={`¿Estás seguro que quieres eliminar la novedad "${newsToDelete?.title}"?`}
            />
        </>
    );
}

export default TableNews;
